<template>
  <!-- AS-1477: в Audit Trail фильтры и «Скачать» видны даже при одной записи
       (после фильтрации SSO-пользователей по ролям записей может остаться мало) -->
  <div
    v-if="(histModel.length > 1 || serverMode) && !loading"
    class="d-flex align-items-center mb-3 gap-3"
  >
    <div style="width: 10em">
      <label class="form-label mb-1">{{ t('history.ui.author') }}</label>
      <MultiSelectDropdown
        :options="authorsList"
        v-model="selectedAuthors"
        buttonWidth="10em"
      />
    </div>
    <div>
      <label class="form-label mb-1">{{ t('history.ui.dateFrom') }}</label>
      <DatePicker v-model="startDate" />
    </div>
    <div>
      <label class="form-label mb-1">{{ t('history.ui.dateTo') }}</label>
      <DatePicker v-model="endDate" />
    </div>
    <div class="align-self-end">
      <button
        class="btn btn-outline-secondary"
        :class="{ 'history-reset-btn-active': hasActiveFilters }"
        :disabled="!hasActiveFilters"
        @click="resetFilters"
      >
        {{ t('history.ui.reset') }}
      </button>
    </div>
    <div v-if="isAuditTrail" class="align-self-end">
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!props.onExport"
        @click="onExportClick"
      >
        {{ t('history.ui.download') }}
      </button>
    </div>
  </div>
  <div
    v-if="loading"
    class="text-center py-4"
  >
    <i class="bi bi-arrow-clockwise spinning"></i>
    <div class="mt-2">{{ t('history.ui.loadingHistory') }}</div>
  </div>
  <div
    v-else-if="isEmpty"
    class="text-center py-4 text-muted"
  >
    {{ t('history.ui.empty') }}
  </div>
  <div
    v-else
    class="history-main"
  >
    <table class="table table-sm history">
      <tbody>
        <template
          v-for="(step, stepIdx) in isAuditTrail ? paginatedModel : filteredModel"
          :key="step.id ?? `${step.entity || 'none'}:${stepIdx}`"
        >
          <tr v-if="step.changes.length">
            <td
              :rowspan="step.changes.length"
              class="fw-semibold"
            >
              {{ step.author }}
            </td>
            <td
              :rowspan="step.changes.length"
              class="text-muted"
            >
              {{ step.date }}
            </td>
            <td :rowspan="step.changes.length">
              <span
                v-if="step.entity"
                v-html="entityText(step)"
              ></span>
              <template v-if="step.entityUpload">
                <span>&nbsp;</span>
                <Docview
                  v-if="isAcceptable(step.entityUpload)"
                  :upload="step.entityUpload"
                  :isLink="true"
                />
                <a
                  v-else
                  :href="downloadLink({ url: step.entityUpload.secure_filename })"
                  target="_blank"
                  rel="noopener noreferrer"
                  :download="step.entityUpload.filename"
                  >{{ step.entityUpload.filename }}</a
                >
              </template>
            </td>
            <td class="text-nowrap">
              <span
                v-if="step.changes[0].action"
                v-html="step.changes[0].action"
              ></span>
            </td>
            <td style="min-width: 250px">
              <span
                v-if="step.changes[0].text"
                v-html="step.changes[0].text"
              ></span>
            </td>
          </tr>

          <tr
            v-for="(chg, idx) in step.changes.slice(1)"
            :key="`${step.id ?? stepIdx}-${idx + 1}`"
          >
            <td class="text-nowrap">
              <span
                v-if="chg.action"
                v-html="chg.action"
              ></span>
            </td>
            <td style="min-width: 250px">
              <span
                v-if="chg.text"
                v-html="chg.text"
              ></span>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <Pagination
      v-if="isAuditTrail"
      :totalItem="serverMode ? (serverTotal ?? 0) : filteredModel.length"
      :currentPage="currentPage"
      :onPage="onPage"
      @pageChange="handlePageChange"
    />
  </div>
</template>

<script setup>
import { computed, isReactive, isRef, toValue, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DOMPurify from 'dompurify'
import { get as _get, result as _result, isObject } from 'lodash-es'
import { models, fields, values, ltmsPublicBaseUrl } from '@/lib/history'
import { namedEntityLabel } from '@/lib/dropdownLabel.js'
import { uploads as uploadDivisionLabels } from '@/lib/label'
import { commons } from '@/lib/transformer'
import MultiSelectDropdown from '@/components/dropdowns/MultiSelectDropdown.vue'
import DatePicker from '@/components/datepicker.vue'
import Pagination from '@/components/pagination/pagination.vue'
import Docview from '@/components/docviewer/button.vue'
import { isAcceptable } from '@/components/docviewer/view.js'
import { downloadLink } from '@/composables/api'
import { historyAction, historyFieldLabel, historyPhrase } from '@/lib/historyPhrases'
import { parse } from 'date-fns'
const { t } = useI18n()
const props = defineProps({
  list: Object,
  loading: { type: Boolean, default: true },
  limit: { type: Number, default: null },
  modelName: String,
  modelId: Number,
  /** Callback для выгрузки Audit Trail (Excel). */
  onExport: { type: Function, default: null },
  /** Audit Trail: серверная пагинация/фильтры — запрос страницы с бэка. */
  onQueryChange: { type: Function, default: null },
  serverTotal: { type: Number, default: null },
  serverAuthors: { type: Array, default: null },
})
const histModel = computed(() => _pick(props.list ?? {}))
const isEmpty = computed(() => histModel.value && histModel.value.length === 0)
const currentPage = ref(1)
const onPage = ref(20)
defineExpose({
  resetFilters,
  getExportParams: () => ({
    authors: [...selectedAuthors.value],
    dateFrom: startDate.value,
    dateTo: endDate.value,
  }),
})

const isAuditTrail = computed(() => props.modelName === 'audit_trail')
const serverMode = computed(
  () => isAuditTrail.value && typeof props.onQueryChange === 'function'
)

const handlePageChange = ({ page, onPage: newOnPage }) => {
  currentPage.value = page
  onPage.value = Number(newOnPage)
  if (serverMode.value) {
    props.onQueryChange({ page: currentPage.value, onPage: onPage.value })
  }
}
const selectedAuthors = ref([])
const startDate = ref(null)
const endDate = ref(null)

const hasActiveFilters = computed(
  () => selectedAuthors.value.length > 0 || startDate.value != null || endDate.value != null
)

const authorsList = computed(() => {
  if (serverMode.value && props.serverAuthors) {
    return props.serverAuthors.map(a => ({ value: a, label: a }))
  }
  const uniq = Array.from(new Set(histModel.value.map(s => s.author)))
  return uniq.sort().map(a => ({ value: a, label: a }))
})

function onExportClick() {
  if (typeof props.onExport === 'function') {
    props.onExport()
  }
}

const skipFilterQuery = ref(false)

function resetFilters({ emitQuery = true } = {}) {
  skipFilterQuery.value = !emitQuery
  selectedAuthors.value = []
  startDate.value = null
  endDate.value = null
  currentPage.value = 1
  skipFilterQuery.value = false
}

watch(
  [selectedAuthors, startDate, endDate],
  () => {
    if (skipFilterQuery.value) return
    currentPage.value = 1
    if (serverMode.value) {
      props.onQueryChange({
        page: 1,
        authors: [...selectedAuthors.value],
        dateFrom: startDate.value,
        dateTo: endDate.value,
      })
    }
  },
  { flush: 'sync' }
)

function parseStepDate(str) {
  let d = parse(str, 'dd.MM.yyyy, HH:mm:ss', new Date())
  if (Number.isNaN(d.getTime())) d = parse(str, 'dd.MM.yyyy, HH:mm', new Date())
  return d
}

const filteredModel = computed(() => {
  if (serverMode.value) {
    return histModel.value
  }
  return histModel.value.filter(step => {
    if (selectedAuthors.value.length > 0 && !selectedAuthors.value.includes(step.author)) {
      return false
    }
    const d = parseStepDate(step.date)
    if (startDate.value) {
      const from = new Date(startDate.value)
      from.setHours(0, 0, 0)
      if (d < from) return false
    }
    if (endDate.value) {
      const to = new Date(endDate.value)
      to.setHours(23, 59, 59)
      if (d > to) return false
    }
    return true
  })
})

// AS-1477: страница не должна выходить за пределы данных — при смене
// набора записей (например, переключение сервиса в Audit Trail) прижимаем
// текущую страницу к последней доступной, иначе список выглядит пустым.
// В serverMode длина histModel — только текущая страница с бэка; лимит страниц
// берём из total_count, иначе currentPage постоянно сбрасывается в 1.
watch(
  () =>
    serverMode.value
      ? Number(props.serverTotal ?? 0)
      : filteredModel.value.length,
  total => {
    if (serverMode.value && props.serverTotal == null) return
    const maxPage = Math.max(1, Math.ceil(total / onPage.value) || 1)
    if (currentPage.value > maxPage) {
      currentPage.value = maxPage
    }
  }
)

const paginatedModel = computed(() => {
  if (serverMode.value) {
    return filteredModel.value
  }
  const start = (currentPage.value - 1) * onPage.value
  const end = start + onPage.value
  return filteredModel.value.slice(start, end)
})

function formatDiff(ch) {
  const chChanges = Array.isArray(ch?.changes) ? ch.changes : []
  let rel = _result(this, [ch.model_name, ch.model_id])
  if (rel == null && this?.[ch.model_name] != null) {
    rel = _result(this, [ch.model_name, String(ch.model_id)])
  }
  if (!rel && Array.isArray(this[ch.model_name])) {
    rel = this[ch.model_name].find(
      d => d?.id === ch.model_id || String(d?.id) === String(ch.model_id)
    )
  }
  const fmt = { entity: null, key: `${ch.model_name}:${ch.model_id}` }
  const changes = []

  switch (ch.model_name) {
    case 'Activities':
    case 'ActivityTypes':
    case 'Comments':
    case 'Ticket':
    case 'TicketComment':
    case 'Consumables':
    case 'ConsumablesExpenses':
    case 'ConsumablesPurchases':
    case 'ConsumablesStock':
    case 'Devices':
    case 'DevicesExpenses':
    case 'DevicesPurchases':
    case 'DevicesStock':
    case 'MaterialsComm':
    case 'MaterialsCommExpenses':
    case 'MaterialsCommPurchases':
    case 'MaterialsCommStock':
    case 'MaterialsNd':
    case 'MaterialsNdExpenses':
    case 'MaterialsNdPurchases':
    case 'MaterialsNdStock':
    case 'Medicines':
    case 'MedicinesExpenses':
    case 'MedicinesPurchases':
    case 'MedicinesStock':
    case 'MedicineTrademarks':
    case 'Orders':
    case 'Destructions':
    case 'Templates':
    case 'Uploads':
    case 'User':
    case 'AssignedUsers':
    case 'Vendors':
    case 'Manufacturers':
    case 'Dropdowns':
    case 'DropdownsCatalog':
      if (ch.model_name.endsWith('Purchases')) {
        if (rel && rel.length) {
          rel = JSON.parse(rel)
        }
      }
      if (ch.model_name.endsWith('Destructions') || ch.model_name.endsWith('Expenses')) {
        if (rel && typeof rel === 'string') {
          rel = JSON.parse(rel)
        }
      }
      // В истории заявки номер заказа без ссылки — она вела бы на ту же страницу
      const linkOrder = props.modelName !== 'Orders'
      fmt.entity = ch.model_name.endsWith('Expenses')
        ? models[ch.model_name].entityName(ch, rel, linkOrder)
        : models[ch.model_name].entityName(ch, rel, isAuditTrail.value)
      break
    case 'MedicineManufacturer':
      // For MedicineManufacturer, pass the entire related object (this) to access all data
      fmt.entity = models[ch.model_name].entityName(ch, this, isAuditTrail.value)
      break
    case 'EmailAddress':
    case 'Communication':
    case 'Departments':
    case 'Laboratories':
    case 'WriteOffs':
    case 'Complaints':
    case 'Receptions':
    case 'ActionRequests':
    case 'ConsumablesDestructions':
    case 'DevicesDestructions':
    case 'MaterialsCommDestructions':
    case 'MaterialsNdDestructions':
    case 'MedicinesDestructions':
      fmt.entity = models[ch.model_name].entityName(ch, this, isAuditTrail.value)
      break
    default:
      fmt.entity = historyPhrase('entity.generic', { model: ch.model_name, id: ch.model_id })
  }

  // Reclassified: одна строка «перемещена из» + стандартное описание старой позиции со ссылкой/id
  if (ch.comment === 'Reclassified') {
    let oldModelName = null
    let oldModelId = null
    for (const change of chChanges) {
      const path = change?.path || []
      if (path[0] === 'model_name') oldModelName = change.old_value
      else if (path[0] === 'model_id') oldModelId = change.old_value
    }
    if (oldModelName != null && oldModelId != null) {
      const src = formatReclassifiedSource(oldModelName, oldModelId, this)
      if (src) {
        changes.push({
          action: '<span class="text-muted">перемещена из</span>',
          text: DOMPurify.sanitize(src),
        })
      }
    }
    fmt.changes = changes
    return fmt
  }

  const excludeFields = [
    'vortex_log',
    'part_id',
    'unique_id',
    'receiving_date_actual_vortex',
    'related_stocks',
    'assigned_users',
    'vortex_part_id',
    'parent_part_id',
    'return_part_id',
    'vortex_gtd_number',
    'vortex_awb_number',
    // AS-908: техсвязь «закупка → расход» у *Purchases
    'expense',
    'purchase',
    'activity_id',
  ]

  const isActivitiesHiddenField = field =>
    ch.model_name === 'Activities' && ['activity_id', 'created_by'].includes(field)

  const shouldExcludeField = field =>
    excludeFields.includes(field) || isActivitiesHiddenField(field)

  // Откат файла: в API это Rollback по Uploads (поля связи) или file_added/delete_object.
  // Технические diff'ы и числовые path иначе выкидывают строку целиком.
  if (ch.comment === 'Rollback' && ch.model_name === 'Uploads') {
    const src = chChanges.find(change => change?.filename) || chChanges[0] || {}
    if (pushRollbackUploadChange(changes, ch, src, rel)) {
      fmt.changes = changes
      return fmt
    }
  }

  // Проверяем, содержат ли все изменения только цифры в path (костыль для фильтрации).
  // Исключение: registration_certificates[i].* — там индекс в path ожидаем.
  const hasOnlyNumericPaths =
    chChanges.length > 0 &&
    chChanges.every(change => {
      if (!Array.isArray(change.path)) return false
      if (change.path[0] === 'registration_certificates') return false
      return change.path.some(segment => /^\d+$/.test(String(segment)))
    })

  // Если все изменения содержат цифры в path, пропускаем их
  if (hasOnlyNumericPaths) {
    return { entity: null, key: `${ch.model_name}:${ch.model_id}`, changes: [] }
  }

  for (const change of chChanges) {
    change.model_id = ch.model_id
    change.model_name = ch.model_name
    if (isHiddenTechnicalUploadChange(change)) continue
    if (
      ch.comment === 'Rollback' &&
      change.filename &&
      ['file_added', 'delete_object', 'add_object'].includes(change.action)
    ) {
      pushRollbackUploadChange(changes, ch, change, rel)
      continue
    }
    const cfmt = {}
    const iobj = this?.initial?.[ch.model_name]?.find(obj => obj.id === ch.model_id)
    const hasAmountStatus = iobj && 'amount' in iobj && 'status' in iobj
    const skipAmountStatus = iobj && iobj.amount === 0 && iobj.status?.value == null
    const exp_text =
      hasAmountStatus && !skipAmountStatus
        ? ` ${_get(fields, ['amount'])} ${iobj.amount}, ${_get(fields, ['status'])} ${iobj.status?.value}`
        : ''

    // Для Rollback сначала добавляем запись "отмена действия:"
    if (
      ch.comment === 'Rollback' &&
      (change.action === 'values_changed' || change.action === 'type_changes')
    ) {
      if (!shouldExcludeField(change.path[0])) {
        const rollbackField = resolveHistoryFieldLabel(ch.model_name, change.path, change)
        if (!(rollbackField instanceof Function)) {
          let fieldName = historyFieldLabel(
            ch.model_name,
            change.path,
            rollbackField || change.path.join('.')
          )
          if (fieldName == change.path?.[0]) {
            const req_field = searchFieldset(this, fieldName)
            if (req_field) {
              fieldName = req_field
            }
          }

          const val = getVal(ch, change, this, 'value')
          const oldVal = getVal(ch, change, this, 'old_value')
          if (oldVal) {
            const valHtml = val
              ? DOMPurify.sanitize(`<span class="text-success d-inline-block">${val}</span>`)
              : ''
            const oldValHtml = DOMPurify.sanitize(
              `<span class="text-danger d-inline-block">${oldVal}</span>`
            )
            const rollbackText = val
              ? historyPhrase('actions.rollbackTextTo', { field: fieldName, value: valHtml, oldValue: oldValHtml })
              : historyPhrase('actions.rollbackText', { field: fieldName, oldValue: oldValHtml })
            changes.push({
              action: historyAction('rollbackCancel'),
              text: rollbackText,
            })
          }
        }
      }
    }

    switch (change.action) {
      case 'values_changed':
      case 'type_changes':
      case 'exchanged':
        if (shouldExcludeField(change.path[0])) continue

        cfmt.field = resolveHistoryFieldLabel(ch.model_name, change.path, change)
        if (!(cfmt.field instanceof Function)) {
          if (!cfmt.field) {
            cfmt.field = change.path.join('.')
          }
          cfmt.field = historyFieldLabel(ch.model_name, change.path, cfmt.field)

          const newVal = getVal(ch, change, this)
          const oldVal = getVal(ch, change, this, 'old_value')
          if ((newVal == null || newVal === '') && (oldVal == null || oldVal === '')) continue

          cfmt.action = historyAction('fieldChanged')
          cfmt.val = DOMPurify.sanitize(
            `<span class="text-success d-inline-block">${newVal == null || newVal === '' ? '—' : newVal}</span>`
          )
          cfmt.old_val = null
          if (oldVal)
            cfmt.old_val = DOMPurify.sanitize(
              `<span class="text-danger d-inline-block">${oldVal}</span>`
            )
        }

        break
      case 'active_change': {
        const raw = change.value
        const s = String(raw ?? '').trim().toLowerCase()
        const isActive = raw === true || raw === 1 || s === 'true' || s === '1' || s === 'yes'
        cfmt.action = isActive
          ? '<span class="text-muted">активирован</span>'
          : '<span class="text-muted">деактивирован</span>'
        break
      }
      case 'add_object':
        cfmt.action = historyAction('added', { text: exp_text })
        if (ch.model_name === 'AssignedUsers') {
          cfmt.field = _get(fields, [ch.model_name, 'assigned_user'])
        }
        else if (ch.model_name === 'Uploads') {
          cfmt.field = () => formatUploadAddedDetails(change)
        }
        else if (ch.model_name == 'MedicineTrademarks' && isMeaningfulComment(ch.comment)) {
          // показываем только осмысленный комментарий (например, про объединение
          // марок); служебные Created/Changed не выводим (AS-1477)
          cfmt.field = () => DOMPurify.sanitize(ch.comment)
        }
        break
      case 'initial_value': {
        if (isAuditTrail.value) continue
        if (ch.model_name === 'ActionRequests') continue
        if (shouldExcludeField(change.path[0])) continue

        cfmt.field = resolveHistoryFieldLabel(ch.model_name, change.path, change)
        if (!(cfmt.field instanceof Function)) {
          if (!cfmt.field) {
            cfmt.field = change.path.join('.')
          }
          cfmt.action = historyAction('initialValue')
          // Стандартный резолвер значения (справочники, статусы, связи);
          // если поле без резолвера (осталcя сырой id) — берём display с бэкенда,
          // а если и его нет — показываем сырое значение, но не пустоту
          let valText = getVal(ch, change, this)
          if (
            change.display &&
            (valText === undefined || valText === null || String(valText) === String(change.value))
          ) {
            valText = change.display
          }
          if (valText === undefined || valText === null || valText === '') {
            valText = change.value ?? ''
          }
          cfmt.val = DOMPurify.sanitize(
            `<span class="text-success d-inline-block">${valText}</span>`
          )
          cfmt.old_val = null
        }
        break
      }
      case 'delete_object':
        cfmt.action = historyAction('deleted')
        if (ch.model_name === 'Uploads') {
          cfmt.customUpload = buildUploadForViewer(ch, change, rel)
        }
        else if (ch.model_name == 'MedicineTrademarks' && isMeaningfulComment(ch.comment)) {
          cfmt.field = () => DOMPurify.sanitize(ch.comment)
        }
        break
      case 'iterable_item_removed':
      case 'iterable_item_added':
      case 'dictionary_item_added':
        cfmt.iterable = true
        cfmt.field = resolveHistoryFieldLabel(ch.model_name, change.path, change)
        cfmt.val = DOMPurify.sanitize(
          `<span class="text-danger d-inline-block">${getVal(ch, change, this)}</span>`
        )
        if (change.action == 'iterable_item_removed') {
          cfmt.action = historyAction('removedItems')
        } else {
          cfmt.action = historyAction('addedItems')
        }
        break
      case 'file_added':
        cfmt.action = historyAction('fileAdded')
        if (change.filename) {
          cfmt.field = () => DOMPurify.sanitize(String(change.filename))
        }
        break
      case 'stock_dispatched':
        cfmt.action = historyAction('dispatched')
        break
      case 'stock_destructed':
        cfmt.action = historyAction('destructed')
        break
      case 'declined':
        if (ch.model_name !== 'OfficialReplies')
          cfmt.action = historyAction('declined')
        break
      case 'approved':
        cfmt.action = historyAction('approved')
        break
      case 'password_change':
        cfmt.action = historyAction('passwordChanged')
        break
      case 'graph_account_disabled':
        cfmt.action = '<span class="text-muted">деактивирован (Microsoft Entra)</span>'
        break
      case 'closed':
        cfmt.action =
          ch.model_name === 'WriteOffs'
            ? historyAction('closedSignedAct')
            : historyAction('closed')
        break
      case 'approve_request':
        cfmt.action = historyAction('approveRequest')
        break
      case 'comment_edited':
        cfmt.action = historyAction('commentEdited')
        cfmt.field = () => DOMPurify.sanitize(ch.comment || '')
        break
      case 'comment_added':
        cfmt.action = historyAction('commentAdded')
        cfmt.field = () => DOMPurify.sanitize(ch.comment || '')
        break
      case 'comment_deleted':
        cfmt.action = historyAction('commentDeleted')
        cfmt.field = () => DOMPurify.sanitize(ch.comment || '')
        break
      case 'archived':
        cfmt.action = historyAction('archived')
        break
      case 'order_act_pp_replaced':
        cfmt.action = '<span class="text-muted">заменено</span>'
        cfmt.field = () => formatActPpReplacedDetails(change)
        break
      case 'order_act_pp_added':
        cfmt.action = '<span class="text-muted">добавлено</span>'
        cfmt.field = () => formatActPpAddedDetails(change)
        break
      case 'order_act_pp_date_changed':
        cfmt.action = '<span class="text-muted">изменено поле:</span>'
        cfmt.field = () => formatActPpDateChangedDetails(change)
        break
      case 'add_suborder':
        cfmt.action = historyAction('suborderAdded', { link: `<a href="/orders/${change.value}">${change.value}</a>` })
        break
      case 'remove_suborder':
        cfmt.action = historyAction('suborderRemoved', { link: `<a href="/orders/${change.value}">${change.value}</a>` })
        break
      case 'auth_login':
        cfmt.action = historyAction('login')
        break
      case 'auth_logout':
        cfmt.action = historyAction('logout')
        break
      case 'users_updating_request_sent':
        cfmt.action = historyAction('actualizationRequest')
        cfmt.field = () => {
          let p = null
          try { p = JSON.parse(change?.value || '') } catch { p = null }
          const items = Array.isArray(p?.items) ? p.items : []
          const svc = p?.service ? DOMPurify.sanitize(String(p.service).toUpperCase()) : ''
          const head = svc ? historyPhrase('actions.module', { service: `<b>${svc}</b>` }) : ''
          const lines = items
            .map(it => {
              const name = DOMPurify.sanitize(String(it?.fio || it?.user_id || '')).trim()
              const comment = DOMPurify.sanitize(String(it?.comment || '')).trim()
              const tail = comment ? ` (${comment})` : ''
              if (!name && !comment) return ''
              return historyPhrase('actions.userDataOutdated', { name: `<b>${name || '—'}</b>`, tail })
            })
            .filter(Boolean)
          if (!head && !lines.length) return ''
          const list = lines.length ? `<br/>${lines.join('<br/>')}` : ''
          return `${head}${list}`
        }
        break
      default:
        cfmt.action = ''
    }

    if (typeof cfmt.field === 'string') {
      cfmt.field = historyFieldLabel(ch.model_name, change.path, cfmt.field)
    }

    const str = []

    if (cfmt.field instanceof Function) {
      if (!change.relation) {
        change.relation = findClass(this?.map || [], ch.model_name, change.path?.[0] || '')
      }
      const _str = cfmt.field(change, this)
      if (_str) {
        str.push(_str)
      }
    } else {
      //str.push(cfmt.action)
      if (cfmt.field) {
        if (cfmt.field == change.path?.[0]) {
          const req_field = searchFieldset(this, cfmt.field)
          if (req_field) {
            cfmt.field = req_field
          }
        }
        str.push('"' + cfmt.field + '"')
      }
      if (cfmt.old_val) {
        str.push(historyPhrase('actions.from', { value: cfmt.old_val }))
      }
      if (cfmt.val) {
        if (cfmt.iterable) {
          str.push('- ' + toValue(cfmt.val) + '')
        } else {
          str.push(historyPhrase('actions.to', { value: toValue(cfmt.val) }))
        }
      } else if (cfmt.old_val && !cfmt.iterable) {
        str.push('на пустое значение')
      }
    }

    if (cfmt.customUpload) {
      changes.push({ action: cfmt.action, text: '', upload: cfmt.customUpload })
      continue
    }
    changes.push({ action: cfmt.action, text: str.join(' '), isAddObject: change.action === 'add_object' })
  }
  if (ch.model_name === 'Activities' && changes.length > 1) {
    const added = changes.filter(c => c.isAddObject)
    const rest = changes.filter(c => !c.isAddObject)
    fmt.changes = [...rest, ...added].map(({ isAddObject, ...row }) => row)
  } else {
    fmt.changes = changes.map(({ isAddObject, ...row }) => row)
  }
  fmt.entityUpload = changes.find(c => c.upload)?.upload || null
  return fmt
}

/** Комментарий записи истории осмысленный, а не служебный (Created/Changed/Deleted/Rollback). */
function isMeaningfulComment(comment) {
  return !!comment && !['Created', 'Changed', 'Deleted', 'Rollback'].includes(comment)
}

function pushRollbackUploadChange(changes, ch, change, rel) {
  const upload = buildUploadForViewer(ch, change, rel)
  const divisionKey = change?.division
  const divisionLabel = divisionKey
    ? uploadDivisionLabels[divisionKey] || divisionKey
    : ''
  const name = change?.filename || ''
  const parts = []
  if (divisionLabel) parts.push(`«${divisionLabel}»`)
  if (name) parts.push(name)
  const text = parts.length ? DOMPurify.sanitize(parts.join(' ')) : ''
  if (!upload && !text) return false
  changes.push({
    action: '<span class="text-muted">отмена действия:</span>',
    text,
    upload: upload || null,
  })
  return true
}

function buildUploadForViewer(ch, change, rel) {
  const filename = change.filename || ch.changes?.[0]?.filename || rel?.filename || null
  const secureFilename =
    change.secure_filename || ch.changes?.[0]?.secure_filename || rel?.secure_filename || null
  if (!filename || !secureFilename) return null
  return {
    filename: String(filename),
    secure_filename: String(secureFilename),
    author: rel?.author || { email: '', fio: '', username: '' },
    uploaded_at: rel?.uploaded_at || null,
    version: rel?.version || '',
  }
}

function formatUploadAddedDetails(change) {
  const dateAct = change?.date_act
  if (!dateAct) return ''
  const isActPp =
    change?.division === 'act_pp' ||
    String(change?.action || '').startsWith('order_act_pp')
  const label = isActPp
    ? 'Дата Акта ПП'
    : _get(fields, ['Uploads', 'metadata', 'date_act']) || 'Дата'
  const value = commons.dateLocal(dateAct, 'dd.MM.yyyy') || String(dateAct)
  return DOMPurify.sanitize(
    `"${label}" на <span class="text-success d-inline-block">${value}</span>`
  )
}

function linkifyHistoryUpload(filename, secureFilename) {
  const name = String(filename || '').trim()
  const secure = String(secureFilename || '').trim()
  if (!name) {
    return '—'
  }
  if (!secure) {
    return DOMPurify.sanitize(name)
  }
  return DOMPurify.sanitize(
    `<a href="/api/v1.0/download/${encodeURIComponent(secure)}" target="_blank" rel="noopener noreferrer">${name}</a>`
  )
}

const HIDDEN_UPLOAD_METADATA_KEYS = new Set([
  'ltms_upload_id',
  'ltms_secure_filename',
])

function isHiddenTechnicalUploadChange(change) {
  const path = Array.isArray(change?.path) ? change.path : []
  return path[0] === 'metadata' && HIDDEN_UPLOAD_METADATA_KEYS.has(String(path[1] || ''))
}

function formatActPpLtmsSubmissionSuffix(change) {
  const submissionId = change?.ltms_submission_id
  if (submissionId == null || submissionId === '') {
    return ''
  }
  const base = ltmsPublicBaseUrl()
  return DOMPurify.sanitize(
    ` на основании подачи №<a href="${base}/submissions/${submissionId}" target="_blank" rel="noopener noreferrer">${submissionId}</a>`
  )
}

function formatActPpDateChangedDetails(change) {
  const label = 'Дата Акта ПП'
  const oldVal = change.old_date_act || change.old_value
  const newVal = change.date_act || change.value
  const oldText = oldVal ? commons.dateLocal(oldVal, 'dd.MM.yyyy') || String(oldVal) : ''
  const newText = newVal ? commons.dateLocal(newVal, 'dd.MM.yyyy') || String(newVal) : ''
  const parts = [`"${label}"`]
  if (oldText) {
    parts.push(
      `с <span class="text-danger d-inline-block">${oldText}</span>`
    )
  }
  if (newText) {
    parts.push(
      `на <span class="text-success d-inline-block">${newText}</span>`
    )
  } else if (oldText) {
    parts.push('на пустое значение')
  }
  parts.push(formatActPpLtmsSubmissionSuffix(change))
  return DOMPurify.sanitize(parts.filter(Boolean).join(' '))
}

function formatActPpAddedDetails(change) {
  const parts = [
    `Документ «Акт ПП» ${linkifyHistoryUpload(change.filename || change.value, change.secure_filename)}`,
    formatActPpLtmsSubmissionSuffix(change),
    formatUploadAddedDetails(change) ? ` ${formatUploadAddedDetails(change)}` : '',
  ]
  return parts.filter(Boolean).join('')
}

function formatActPpReplacedDetails(change) {
  const oldLink = linkifyHistoryUpload(change.old_value, change.old_secure_filename)
  const newLink = linkifyHistoryUpload(change.filename || change.value, change.secure_filename)
  const parts = [
    `Акт ПП с ${oldLink} на ${newLink}`,
    formatActPpLtmsSubmissionSuffix(change),
    formatUploadAddedDetails(change) ? ` ${formatUploadAddedDetails(change)}` : '',
  ]
  return parts.filter(Boolean).join('')
}

function entityText(step) {
  if (!step?.entity) return ''
  if (!step.entityUpload?.filename) return step.entity
  const fn = step.entityUpload.filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return step.entity
    .replace(new RegExp(`\\s*${fn}\\s*$`), '')
    .replace(/\s+/g, ' ')
    .trim()
}

function searchFieldset(data, fieldName) {
  if (!data.RequestCategory) {
    return null
  }
  const request_data = data.RequestCategory

  const mergeFieldsets = request_data => {
    return request_data.reduce((acc, category) => {
      return { ...acc, ...category.fieldset }
    }, {})
  }

  const findFieldValue = fieldName => {
    const mergedFieldset = mergeFieldsets(request_data)
    return mergedFieldset[fieldName] || null
  }

  return findFieldValue(fieldName)
}

/**
 * Подпись поля для истории.
 * Нельзя делать _get(fields, [model, field, index]): если field — строка-лейбл,
 * lodash вернёт символ строки (напр. «е» из «Регистрационные…»).
 */
function normalizeHistoryPath(path) {
  if (Array.isArray(path)) return path
  if (typeof path === 'string' && path.trim()) {
    return path.split('.').map(seg => (/^\d+$/.test(seg) ? Number(seg) : seg))
  }
  return []
}

function formatReclassifiedSource(oldModelName, oldModelId, related) {
  const modelId = Number(oldModelId)
  const id = Number.isFinite(modelId) ? modelId : oldModelId
  const ch = { model_name: oldModelName, model_id: id }
  const cfg = models[oldModelName]
  if (!cfg?.entityName) {
    return `Сущность ${oldModelName}:${id}`
  }

  let rel = _result(related, [oldModelName, id])
  if (rel == null && related?.[oldModelName] != null) {
    rel = _result(related, [oldModelName, String(id)])
  }
  if (rel == null && Array.isArray(related?.[oldModelName])) {
    rel = related[oldModelName].find(d => d?.id === id || String(d?.id) === String(id))
  }

  const isStock = String(oldModelName).endsWith('Stock')
  // catalog history(): related[id] = "name"; full_history: объект с name
  if (!isStock && isObject(rel) && !Array.isArray(rel)) {
    rel = namedEntityLabel(rel) || rel.description || ''
  }
  if (!isStock && (rel == null || rel === '')) {
    rel = String(id)
  }

  let label = cfg.entityName(ch, rel, true)
  if (!label) {
    label = `Сущность ${oldModelName}:${id}`
  }

  // у складских entityName уже есть ссылка с id; у справочников — только имя
  const prefix = cfg.prefix
  const hasIdLink =
    typeof label === 'string' &&
    (label.includes('href=') || (prefix && label.includes(`/${prefix}/`)))
  if (prefix && !hasIdLink) {
    label = `${label} ID=<a href="/${prefix}/${id}">${id}</a>`
  }
  return label
}

function resolveHistoryFieldLabel(modelName, path, change = null) {
  path = normalizeHistoryPath(path)
  if (!path.length) return ''

  if (path[0] === 'registration_certificates') {
    return formatRegistrationCertificatesHistoryLabel(path, change)
  }

  const root = path[0]
  const nested = _get(fields, [modelName, root])
  if (typeof nested === 'function') return nested
  if (typeof nested === 'string') return nested
  const flat = _get(fields, [root])
  if (typeof flat === 'function') return flat
  if (typeof flat === 'string') return flat
  if (path.length > 1) {
    const full = _get(fields, [modelName, ...path]) || _get(fields, path)
    if (typeof full === 'function') return full
    if (typeof full === 'string' && full.length > 1) return full
  }
  return root
}

function formatRegistrationCertificatesHistoryLabel(path, change) {
  const sub = path[2]
  let label = historyFieldLabel('Medicines', 'registration_certificates')
  if (sub === 'date') label = historyFieldLabel('', 'registration_at')
  else if (sub === 'certificate') label = historyFieldLabel('Medicines', 'registration_certificate')
  else {
    // Добавление/удаление всей пары: № РУ уже в значении — не дублировать
    return label
  }

  const ru = (change?.ru_certificate || '').toString().trim()
  if (ru) return historyPhrase('ui.registrationCertificateNamed', { label, ru })

  const idxRaw = path[1]
  const idx = Number(idxRaw)
  if (Number.isFinite(idx) && idx >= 0) {
    return historyPhrase('ui.registrationCertificateIndexed', { label, n: idx + 1 })
  }
  return label
}

function resolveHistoryValueGetter(modelName, path) {
  path = normalizeHistoryPath(path)
  if (!path.length) return null
  const v = values()
  const full = _get(v, [modelName, ...path]) || _get(v, path)
  if (typeof full === 'function') return full
  if (path[0] === 'registration_certificates') {
    if (path[2] === 'date') {
      const dateFmt = _get(v, [modelName, 'registration_at']) || _get(v, ['registration_at'])
      if (typeof dateFmt === 'function') return dateFmt
    }
    const rootRu = _get(v, [modelName, 'registration_certificates']) || _get(v, ['registration_certificates'])
    if (typeof rootRu === 'function') return rootRu
  }
  if (path.length > 1) {
    const root = _get(v, [modelName, path[0]]) || _get(v, [path[0]])
    if (typeof root === 'function') return root
  }
  return null
}

function extractIdFromValue(val) {
  // Обработка словаря с id - сначала пытаемся распарсить строку
  if (typeof val === 'string') {
    try {
      // Пробуем стандартный JSON.parse
      const parsed = JSON.parse(val)
      if (parsed && typeof parsed === 'object' && parsed.id !== undefined) {
        return parsed.id
      }
    } catch {
      // Если не удалось, пробуем заменить одинарные кавычки на двойные (Python dict формат)
      try {
        const normalized = val.replace(/'/g, '"')
        const parsed = JSON.parse(normalized)
        if (parsed && typeof parsed === 'object' && parsed.id !== undefined) {
          return parsed.id
        }
      } catch {
        // Если и это не сработало, пытаемся извлечь id через регулярное выражение
        const idMatch = val.match(/'id'\s*:\s*(\d+)/)
        if (idMatch) {
          return parseInt(idMatch[1], 10)
        }
      }
    }
  } else if (val && typeof val === 'object' && val.id !== undefined) {
    return val.id
  }
  return val
}

function getVal(ch, change, rel, prop = 'value') {
  let res = resolveHistoryValueGetter(ch.model_name, change.path)
  const hasCustomFormatter = typeof res === 'function'
  if (!hasCustomFormatter) {
    // Если форматтер не найден или он не является функцией, используем значение как есть
    res = () => change[prop]
  }

  if ('value' in change) {
    change.value = extractIdFromValue(change.value)
  }
  if ('old_value' in change) {
    change.old_value = extractIdFromValue(change.old_value)
  }

  let val = res(ch, change, rel, prop)
  if (isRef(val) || isReactive(val)) {
    val = toValue(val)
  }
  // Не отдаём объекты в шаблон — иначе «[object Object]» у ролей/нотификаций
  if (val != null && typeof val === 'object') {
    if (Array.isArray(val)) {
      val = val
        .map(v => {
          if (v == null || typeof v !== 'object') return v
          return v.caption || namedEntityLabel(v) || v.id || ''
        })
        .filter(Boolean)
        .join(', ')
    } else {
      const userLabel = [val.username, val.fio].filter(Boolean).join(', ')
        val = val.caption || namedEntityLabel(val) || val.value || userLabel || (val.id ?? '')
    }
  }

  const class_c = findClass(rel?.map || [], ch.model_name, change.path?.[0] || '')
  if (!hasCustomFormatter && !isNaN(Number(val)) && Number(val) !== 0) {
    const numericVal = Number(val)
    if (
      class_c &&
      Object.prototype.hasOwnProperty.call(rel, class_c) &&
      Array.isArray(rel[class_c])
    ) {
      const class_entries = rel[class_c]
      if (class_c === 'Dropdowns') {
        const field_obj = class_entries.find(d => d.id === numericVal)

        if (field_obj) {
          val = field_obj.value
        }
      } else {
        const field_obj = class_entries.find(d => d.id === numericVal)
        if (field_obj) {
          if (class_c === 'Role') {
            val = field_obj?.caption
          } else {
            val = namedEntityLabel(field_obj) || field_obj?.name
            if (field_obj?.full_name) val = field_obj.full_name
          }
        }
      }
    }
  }
  return val ?? ''
}

function findClass(relMap, modelName, fieldName) {
  const modelEntry = relMap.find(entry => entry.model === modelName)
  if (!modelEntry) return null

  const fieldEntry = modelEntry.fields.find(field => field.field === fieldName)
  return fieldEntry ? fieldEntry.class : null
}

function _pick(raw) {
  const hist = []
  if (!raw || typeof raw !== 'object') {
    return hist
  }
  // на случай, если список передали без нормализации модалки
  let inner = raw
  if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
    inner = raw.data
  }
  const history = Array.isArray(inner.history) ? inner.history : []
  const related =
    inner.related && typeof inner.related === 'object' && !Array.isArray(inner.related)
      ? inner.related
      : {}

  const h = props.limit ? history.slice(0, props.limit) : history.slice()
  for (const batch of h) {
    const batchRows = Array.isArray(batch)
      ? batch
      : batch && typeof batch === 'object'
        ? [batch]
        : []
    if (!batchRows.length) continue
    const first = batchRows[0]
    if (!first || typeof first !== 'object') continue

    const base = {
      date: commons.dateLocal(first.date, 'dd.MM.yyyy, HH:mm'),
      author: first.fio || first.username,
    }

    const items = batchRows.map(formatDiff, related).map(item => ({
      ...item,
      changes: Array.isArray(item.changes)
        ? item.changes.filter(ch => ch.action?.trim() || ch.text?.trim() || ch.upload)
        : [],
    }))

    if (!items.length) continue

    const byEntity = new Map()
    for (const item of items) {
      const { entity, changes, entityUpload, key: histRowKey } = item
      const rowChanges = Array.isArray(changes) ? changes : []
      // Не сшивать по тексту «Объект», а по model_name:id:
      // — дропдауны / Audit Trail;
      // — расходы: две одинаковые позиции (один ЛП) дают одинаковый entity и иначе сливаются.
      const dropdownRow =
        (props.modelName === 'Dropdowns' || props.modelName === 'audit_trail') &&
        typeof histRowKey === 'string' &&
        (histRowKey.startsWith('Dropdowns:') || histRowKey.startsWith('DropdownsCatalog:'))
      const expenseRow =
        typeof histRowKey === 'string' && /Expenses:\d+$/.test(histRowKey)
      const separateById = dropdownRow || expenseRow
      const mapKey = separateById ? histRowKey : entity ?? '__none__'
      if (!byEntity.has(mapKey)) {
        byEntity.set(mapKey, {
          ...base,
          entity,
          mergeKey: separateById ? histRowKey : undefined,
          entityUpload: null,
          changes: [],
        })
      }
      const grouped = byEntity.get(mapKey)
      grouped.changes.push(...rowChanges)
      if (separateById) grouped.entity = entity
      if (!grouped.entityUpload && entityUpload) grouped.entityUpload = entityUpload
    }

    hist.push(...byEntity.values())
  }

  // audit_trail и история выпадающих списков: одна операция — несколько полей → одна строка в таблице
  if (props.modelName === 'audit_trail' || props.modelName === 'Dropdowns') {
    const dedupeKey = row => {
      if (row.mergeKey) {
        return `${row.date}|${row.author}|${row.mergeKey}`
      }
      return `${row.date}|${row.author}|${(row.entity ?? '').trim()}`
    }
    const merged = []
    for (const row of hist) {
      const k = dedupeKey(row)
      const existing = merged.find(m => dedupeKey(m) === k)
      const rowCh = Array.isArray(row.changes) ? row.changes : []
      if (existing) {
        existing.changes.push(...rowCh)
        if (row.mergeKey && row.entity) existing.entity = row.entity
      } else {
        merged.push({ ...row, changes: [...rowCh] })
      }
    }
    return merged
  }

  return hist
}
</script>

<style scoped>
.history-reset-btn-active {
  color: var(--bs-body-color, #212529);
  border-color: var(--bs-secondary);
}
</style>
